import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, type FormErrors } from "@mantine/form";
import { IconAlertCircle, IconInfoCircle, IconPlus } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import {
  getPanelAdminErrorMessage,
  PanelBillingMode,
  PanelDefinition,
  SavePanelInput,
} from "../../../services/admin/panels.service";
import { scrollToFirstError } from "../../../utils/formValidation";
import { PanelComponentEditor } from "./PanelComponentEditor";
import {
  EMPTY_PANEL_COMPONENT,
  getPanelFormValues,
  normalizeComponentPolicy,
  PanelComponentFormValue,
  PanelFormValues,
  PanelSelectOption,
  toSavePanelInput,
} from "./panelForm";

interface PanelFormModalProps {
  opened: boolean;
  panel?: PanelDefinition;
  stainOptions: PanelSelectOption[];
  cptOptions: PanelSelectOption[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (input: SavePanelInput) => Promise<void>;
}

function validateGroupCpt(value: string, values: PanelFormValues) {
  return values.billingMode !== "individual_only" && !value
    ? "A group CPT is required for this billing mode"
    : null;
}

function validatePanelName(value: string) {
  if (!value.trim()) return "Panel name is required";
  return value.trim().length <= 120
    ? null
    : "Panel name must be 120 characters or fewer";
}

function validateStain(value: string, values: PanelFormValues) {
  if (!value) return "Select a stain";
  const matches = values.components.filter(
    (component) => component.stainId === value
  );
  return matches.length > 1
    ? "Use quantity instead of adding the same stain twice"
    : null;
}

function validateQuantity(value: number) {
  if (!Number.isInteger(Number(value)) || Number(value) < 1) {
    return "Quantity must be a positive whole number";
  }
  return Number(value) <= 100 ? null : "Quantity cannot exceed 100";
}

function validateOverrideCpt(
  value: string,
  values: PanelFormValues,
  path: string
) {
  const index = Number(path.split(".")[1]);
  return values.components[index]?.billingPolicy === "override" && !value
    ? "Select an override CPT"
    : null;
}

export function PanelFormModal({
  opened,
  panel,
  stainOptions,
  cptOptions,
  isSubmitting,
  onClose,
  onSubmit,
}: PanelFormModalProps) {
  const [discardRequested, setDiscardRequested] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const form = useForm<PanelFormValues>({
    initialValues: getPanelFormValues(panel),
    validateInputOnBlur: true,
    validate: {
      name: validatePanelName,
      groupCptId: validateGroupCpt,
      components: {
        stainId: validateStain,
        quantity: validateQuantity,
        overrideCptId: validateOverrideCpt,
      },
    },
  });
  const isEditing = useMemo(() => Boolean(panel), [panel]);
  const modalTitle = useMemo(
    () =>
      panel
        ? `Create version ${panel.currentVersionNumber + 1}`
        : "Create panel",
    [panel]
  );

  const handleBillingModeChange = useCallback(
    (billingMode: string) => {
      const mode = billingMode as PanelBillingMode;
      form.setValues((values) => ({
        ...values,
        billingMode: mode,
        groupCptId: mode === "individual_only" ? "" : values.groupCptId,
        components: (values.components ?? form.values.components).map(
          (component) => normalizeComponentPolicy(mode, component)
        ),
      }));
    },
    [form]
  );
  const handleComponentChange = useCallback(
    (index: number, value: PanelComponentFormValue) => {
      form.setFieldValue(`components.${index}`, value);
    },
    [form]
  );
  const handleComponentRemove = useCallback(
    (index: number) => form.removeListItem("components", index),
    [form]
  );
  const handleAddComponent = useCallback(() => {
    const component = normalizeComponentPolicy(form.values.billingMode, {
      ...EMPTY_PANEL_COMPONENT,
    });
    form.insertListItem("components", component);
  }, [form]);
  const handleInvalidSubmit = useCallback(
    (errors: FormErrors) => {
      scrollToFirstError(form, errors);
    },
    [form]
  );
  const handleValidSubmit = useCallback(
    async (values: PanelFormValues) => {
      setSubmitError("");
      try {
        await onSubmit(toSavePanelInput(values));
      } catch (error) {
        setSubmitError(getPanelAdminErrorMessage(error));
      }
    },
    [onSubmit]
  );
  const handleRequestClose = useCallback(() => {
    if (form.isDirty()) {
      setDiscardRequested(true);
      return;
    }
    onClose();
  }, [form, onClose]);
  const handleKeepEditing = useCallback(() => {
    setDiscardRequested(false);
  }, []);
  const handleDiscard = useCallback(() => {
    setDiscardRequested(false);
    onClose();
  }, [onClose]);
  const handleModalSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      form.onSubmit(handleValidSubmit, handleInvalidSubmit)(event);
    },
    [form, handleInvalidSubmit, handleValidSubmit]
  );

  return (
    <Modal.Root
      opened={opened}
      onClose={handleRequestClose}
      closeOnClickOutside={false}
      closeOnEscape={!isSubmitting}
      size="xl"
      centered
    >
      <Modal.Overlay />
      <Modal.Content
        style={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}
      >
        <Modal.Header>
          <Modal.Title>{modalTitle}</Modal.Title>
          <Modal.CloseButton disabled={isSubmitting} />
        </Modal.Header>
        <Modal.Body style={{ overflowY: "auto" }}>
          <Box
            component="form"
            id="panel-form"
            noValidate
            onSubmit={handleModalSubmit}
          >
            <Stack gap="lg">
              {submitError && (
                <Alert
                  icon={<IconAlertCircle size={18} />}
                  title="Panel could not be saved"
                  color="red"
                  variant="light"
                  role="alert"
                >
                  {submitError}
                </Alert>
              )}
              {panel?.isSystemManaged && (
                <Alert
                  icon={<IconInfoCircle size={18} />}
                  title="System-managed panel"
                  color="blue"
                  variant="light"
                >
                  Tissue Cypher keeps its protected workflow and required one
                  H&amp;E plus eight unstained slides per block. Billing changes
                  create a new version for future orders; existing orders do not
                  change.
                </Alert>
              )}
              {isEditing && !panel?.isSystemManaged && (
                <Alert
                  icon={<IconInfoCircle size={18} />}
                  title="Versioned change"
                  color="blue"
                  variant="light"
                >
                  Existing orders retain their original configuration. New
                  orders use this version after it is saved.
                </Alert>
              )}
              <TextInput
                label="Panel name"
                placeholder="e.g. MMR panel"
                maxLength={120}
                withAsterisk
                {...form.getInputProps("name")}
              />
              <Textarea
                label="Description"
                placeholder="Explain when this panel is used"
                minRows={2}
                {...form.getInputProps("description")}
              />
              <TextInput
                label="Workflow"
                value={
                  panel?.workflowKind === "tissue_cypher"
                    ? "Tissue Cypher"
                    : "Ordinary panel"
                }
                disabled
                description="Special workflows are assigned by the system."
              />
              <Radio.Group
                label="Billing mode"
                description="Choose how the panel and its component slides contribute CPT codes."
                withAsterisk
                value={form.values.billingMode}
                onChange={handleBillingModeChange}
              >
                <Stack gap="xs" mt="xs">
                  <Radio value="group_only" label="Group CPT only" />
                  <Radio
                    value="group_plus_individual"
                    label="Group CPT plus selected individual CPTs"
                  />
                  <Radio value="individual_only" label="Individual CPTs only" />
                </Stack>
              </Radio.Group>
              {form.values.billingMode !== "individual_only" && (
                <Select
                  label="Group CPT"
                  description="Added once for each ordered panel, regardless of block count."
                  placeholder="Select a CPT code"
                  data={cptOptions}
                  searchable
                  clearable
                  withAsterisk
                  {...form.getInputProps("groupCptId")}
                />
              )}
              <Stack gap="sm">
                <Group justify="space-between">
                  <div>
                    <Text fw={600}>Components</Text>
                    <Text size="sm" c="dimmed">
                      Quantities are created for every block in the selected
                      specimen.
                    </Text>
                  </div>
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    onClick={handleAddComponent}
                    disabled={panel?.isSystemManaged}
                  >
                    Add component
                  </Button>
                </Group>
                {form.values.components.map((component, index) => (
                  <PanelComponentEditor
                    key={form.key(`components.${index}`)}
                    index={index}
                    value={component}
                    billingMode={form.values.billingMode}
                    stainOptions={stainOptions}
                    cptOptions={cptOptions}
                    errors={{
                      stainId: form.errors[
                        `components.${index}.stainId`
                      ] as string,
                      quantity: form.errors[
                        `components.${index}.quantity`
                      ] as string,
                      overrideCptId: form.errors[
                        `components.${index}.overrideCptId`
                      ] as string,
                    }}
                    canRemove={form.values.components.length > 1}
                    materialLocked={panel?.isSystemManaged}
                    onChange={handleComponentChange}
                    onRemove={handleComponentRemove}
                  />
                ))}
              </Stack>
              {discardRequested && (
                <Alert
                  icon={<IconAlertCircle size={18} />}
                  title="Discard unsaved changes?"
                  color="orange"
                  variant="light"
                >
                  <Stack gap="sm">
                    <Text size="sm">
                      Changes made since opening this panel will be lost.
                    </Text>
                    <Group justify="flex-end">
                      <Button variant="default" onClick={handleKeepEditing}>
                        Keep editing
                      </Button>
                      <Button color="red" onClick={handleDiscard}>
                        Discard changes
                      </Button>
                    </Group>
                  </Stack>
                </Alert>
              )}
            </Stack>
          </Box>
        </Modal.Body>
        <Group
          justify="space-between"
          p="md"
          style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
        >
          <Button variant="default" onClick={handleRequestClose}>
            Cancel
          </Button>
          <Button type="submit" form="panel-form" loading={isSubmitting}>
            {isEditing ? "Create new version" : "Create panel"}
          </Button>
        </Group>
      </Modal.Content>
    </Modal.Root>
  );
}
